import React from "react";
import { useTranslation } from "react-i18next";
import ERPSGrid from "./ERPSGrid";
import { renderCardLayout } from "../../../utils/cardLayoutRenderer";
import { ResponsiveBox, useMediaQuery, useTheme } from "../../primitives";

/**
 * ResponsiveDataGrid - A wrapper component that automatically switches between
 * table and card layouts based on screen size for WCAG 1.4.10 Reflow compliance.
 * 
 * This component ensures tables are accessible at 400% zoom by switching to a
 * card layout on smaller viewports (below 'md' breakpoint - 1280px).
 * 
 * @component
 * @example
 * ```jsx
 * // Simple usage - no translation function needed!
 * <ResponsiveDataGrid
 *   data={{
 *     rows: [...],
 *     columns: [...],
 *     count: 100
 *   }}
 *   tableLabelText={t("your.label")}
 * />
 * 
 * // Advanced usage with custom config
 * <ResponsiveDataGrid
 *   data={{
 *     rows: [...],
 *     columns: [...],
 *     count: 100
 *   }}
 *   cardConfig={{
 *     fields: [
 *       { field: 'id', label: 'ID', primary: true },
 *       { field: 'name', label: 'Name' }
 *     ]
 *   }}
 *   tableLabelText="List of items"
 * />
 * ```
 */
const ResponsiveDataGrid = (props) => {
  const {
    data,
    cardConfig,
    breakpoint = 'md', // Default breakpoint for responsive switch
    renderCustomCard, // Optional: Custom card renderer function
    cardLayoutProps = {}, // Additional props for card layout
    showCardLayoutOnly = false, // Force card layout regardless of screen size
    t: translationFn, // Optional: Translation function (auto-detected if not provided)
    ...erpsGridProps // All other props pass through to ERPSGrid
  } = props;

  const theme = useTheme();
  const { t: autoTranslation } = useTranslation();
  
  // Use provided translation function or auto-detected one
  const t = translationFn || autoTranslation;
  
  // WCAG 1.4.10 Reflow - Detect viewport width for responsive layout
  // Breakpoint at 1280px supports 400% zoom on 1280px viewport (effectively 320px)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(breakpoint));

  // Determine if we should show card layout
  const shouldShowCardLayout = showCardLayoutOnly || isSmallScreen;

  // Validate required props
  if (!data || !data.rows || !data.columns) {
    console.error('ResponsiveDataGrid: data.rows and data.columns are required');
    return null;
  }

  // Card layout rendering
  if (shouldShowCardLayout) {
    // Use custom card renderer if provided
    if (renderCustomCard && typeof renderCustomCard === 'function') {
      return (
        <ResponsiveBox sx={{ width: '100%' }} {...cardLayoutProps}>
          {data.rows.map((row, index) => renderCustomCard(row, index, data.columns))}
        </ResponsiveBox>
      );
    }

    // Build final card config (merge auto-generated with provided config)
    const autoGeneratedConfig = {
      fields: data.columns
        .filter(col => col.field && col.headerName)
        .map((col, index) => ({
          field: col.field,
          label: col.headerName,
          primary: index === 0, // First field is primary by default
          renderCell: col.renderCell,
        })),
      keyField: 'id', // Default keyField
      ariaLabel: erpsGridProps.tableLabelText || 'Data list',
    };

    // Merge provided cardConfig with auto-generated (provided takes precedence)
    const finalCardConfig = cardConfig 
      ? {
          ...autoGeneratedConfig,
          ...cardConfig,
          fields: cardConfig.fields || autoGeneratedConfig.fields,
        }
      : autoGeneratedConfig;

    return renderCardLayout({
      data: data.rows,
      columns: data.columns,
      cardConfig: finalCardConfig,
      initialLoading: erpsGridProps.initialLoading,
      t: t, // Use auto-detected or provided translation function
      ...cardLayoutProps,
    });
  }

  // Desktop/Table layout - render standard ERPSGrid
  return <ERPSGrid data={data} {...erpsGridProps} />;
};

export default ResponsiveDataGrid;

